import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useRef } from "react";
import {
  DLQInfoResult,
  DLQItem,
  Stage,
} from "../lusca-search-api/generated-src";
import { DlqInfoApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import { DlqStatus } from "../components/dlq/DlqStatusIndicator";

export interface DlqInfo {
  status: DlqStatus;
  dlqItems?: DLQItem[];
  error: Error | null;
}

/** What the /dlq-info lookup is keyed on. */
type DlqLookup =
  | { kind: "orderId"; value: string }
  | { kind: "dlqPk"; value: string };

/*
 * Shared lifecycle for the /dlq-info Athena lookup:
 * 1. Initial call starts an Athena query and returns a queryExecutionId
 * 2. Performs immediate status check in case results are in Athena's cache
 * 3. If not immediately available, polls every second until query completes
 */
function useDlqLookup(stage: Stage, lookup: DlqLookup): DlqInfo {
  const DlqApi = DlqInfoApiFactory();
  const queryClient = useQueryClient();
  const hasTriggeredImmediateFetch = useRef(false);
  const queryKey = ["dlqInfo", stage, lookup.kind, lookup.value];

  const { data: result, error: queryError } = useQuery<DLQInfoResult, Error>(
    queryKey,
    async () => {
      // Check cache for existing queryExecutionId from previous API responses
      // This is important for checking status of an ongoing query
      const currentData = queryClient.getQueryData<DLQInfoResult>(queryKey);
      const queryExecutionId = currentData?.queryExecutionId;

      // First call (queryExecutionId is undefined):
      // - Initiates the DLQ query
      // - Returns a queryExecutionId
      //
      // Subsequent calls (queryExecutionId exists):
      // - Uses queryExecutionId to check status of the running query
      // - Returns current state (PENDING, COMPLETED, etc.)
      // For pk lookups the dlqPk is always sent so the backend keeps the
      // snapshot item even when it has already left the live DLQ table.
      const { data }: AxiosResponse<DLQInfoResult> = await DlqApi.getDLQInfo(
        stage,
        lookup.kind === "orderId" && !queryExecutionId
          ? lookup.value
          : undefined,
        lookup.kind === "dlqPk" ? lookup.value : undefined,
        queryExecutionId
      );

      return data;
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!stage && !!lookup.value,
      refetchInterval: (data) => {
        // Once we have a PENDING status, we need to keep polling the query result
        if (data?.status === "PENDING") {
          // Special handling for when we first get a queryExecutionId
          // Performs immediate check for results incase Athena has it cached,
          // instead of waiting for the normal 1-second polling interval
          if (data.queryExecutionId && !hasTriggeredImmediateFetch.current) {
            // We only want to do this immediate check once per query
            hasTriggeredImmediateFetch.current = true;

            setTimeout(() => {
              queryClient.invalidateQueries(queryKey).catch((err) => {
                console.error("Error invalidating queries:", err);
              });
            }, 0);
          }

          // If result wasn't in Athena's cache, keep polling every second
          // until we get a final status
          return 1000;
        }
        return false; // Once we're no longer PENDING, stop polling
      },

      onSuccess: (data) => {
        if (data.status === "PENDING" && data.queryExecutionId) {
          queryClient.setQueryData<DLQInfoResult>(queryKey, (oldData) => ({
            ...oldData,
            ...data,
          }));
        }
      },
      onError: () => {
        queryClient.setQueryData<DLQInfoResult>(
          queryKey,
          () => ({ status: "FAILED" } as DLQInfoResult)
        );
      },
    }
  );

  const status: DlqStatus = (() => {
    if (queryError) return "error";
    if (!result) return "loading";

    switch (result.status) {
      case "PENDING":
        return "loading";
      case "COMPLETED":
        return result.inDLQ ? "inDlq" : "notInDlq";
      case "ERROR":
        return "error";
      default:
        return "error";
    }
  })();

  return {
    status,
    dlqItems: result?.dlqItems,
    error: queryError || null,
  };
}

/** DLQ items currently in the live DLQ table for a transaction / order. */
export default function useFetchDlqInfo(
  stage: Stage,
  orderId: string
): DlqInfo {
  return useDlqLookup(stage, { kind: "orderId", value: orderId });
}

/**
 * A single DLQ event by its primary key. Unlike the order lookup, the item is
 * returned even when it has since been redriven; `status` is "inDlq" only if
 * it is still present in the live DLQ table.
 */
export function useFetchDlqEvent(stage: Stage, dlqPk: string): DlqInfo {
  return useDlqLookup(stage, { kind: "dlqPk", value: dlqPk });
}
